import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import Chart from './Chart';

// DrillDown visualization props
export interface DrillDownVisualizationProps {
  /**
   * Initial data to display
   */
  initialData: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string;
      borderWidth?: number;
    }>;
  };
  /**
   * Function to fetch drill-down data when a data point is clicked
   */
  fetchDrillDownData: (label: string, level: number) => Promise<{
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string;
      borderWidth?: number;
    }>;
  }>;
  /**
   * Maximum drill-down level
   */
  maxLevel?: number;
  /**
   * Chart type
   */
  type?: 'bar' | 'line' | 'pie' | 'scatter';
  /**
   * Width of the chart
   */
  width?: number | string;
  /**
   * Height of the chart
   */
  height?: number | string;
  /**
   * Additional class name
   */
  className?: string;
}

// Styled components
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartContainer = styled.div<{ $width: number | string; $height: number | string }>`
  width: ${props => typeof props.$width === 'number' ? `${props.$width}px` : props.$width};
  height: ${props => typeof props.$height === 'number' ? `${props.$height}px` : props.$height};
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const BreadcrumbItem = styled.div<{ $isActive: boolean }>`
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.$isActive ? props.theme?.colors?.primary?.[500] || '#1890FF' : props.theme?.colors?.neutral?.[600] || '#757575'};
  cursor: ${props => props.$isActive ? 'default' : 'pointer'};
  font-weight: ${props => props.$isActive ? 'bold' : 'normal'};
  
  &:hover {
    color: ${props => props.$isActive ? props.theme?.colors?.primary?.[500] || '#1890FF' : props.theme?.colors?.primary?.[400] || '#40A9FF'};
  }
  
  &:not(:last-child)::after {
    content: '/';
    margin-left: 0.5rem;
    color: ${props => props.theme?.colors?.neutral?.[500] || '#9E9E9E'};
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;
`;

/**
 * DrillDownVisualization component for interactive data exploration
 * Allows users to click on data points to see more detailed information
 */
const DrillDownVisualization: React.FC<DrillDownVisualizationProps> = ({
  initialData,
  fetchDrillDownData,
  maxLevel = 3,
  type = 'bar',
  width = '100%',
  height = 400,
  className,
}) => {
  const [currentLevel, setCurrentLevel] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState<Array<{ label: string; data: typeof initialData }>>([
    { label: 'Overview', data: initialData }
  ]);
  const [currentData, setCurrentData] = useState(initialData);
  
  const handleDataPointClick = useCallback(async (label: string) => {
    // Don't drill down if we're at the maximum level
    if (currentLevel >= maxLevel) return;
    
    try {
      setIsLoading(true);
      
      // Fetch drill-down data
      const drillDownData = await fetchDrillDownData(label, currentLevel + 1);
      
      // Update state
      setCurrentLevel(currentLevel + 1);
      setCurrentData(drillDownData);
      setBreadcrumbs([
        ...breadcrumbs,
        { label, data: drillDownData }
      ]);
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
      // Handle error (could show an error message)
    } finally {
      setIsLoading(false);
    }
  }, [currentLevel, maxLevel, fetchDrillDownData, breadcrumbs]);
  
  const handleBreadcrumbClick = useCallback((index: number) => {
    // Don't do anything if clicking the current level
    if (index === currentLevel) return;
    
    // Update state
    setCurrentLevel(index);
    setCurrentData(breadcrumbs[index].data);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  }, [currentLevel, breadcrumbs]);
  
  return (
    <Container className={className}>
      <BreadcrumbContainer>
        {breadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem
            key={`${breadcrumb.label}-${index}`}
            $isActive={index === currentLevel}
            onClick={() => handleBreadcrumbClick(index)}
          >
            {breadcrumb.label}
          </BreadcrumbItem>
        ))}
      </BreadcrumbContainer>
      
      <ChartContainer $width={width} $height={height}>
        {isLoading && (
          <LoadingOverlay>
            <div>Loading...</div>
          </LoadingOverlay>
        )}
        
        <Chart
          type={type}
          data={currentData}
          options={{
            onClick: (event: React.MouseEvent, elements: Array<{index: number}>) => {
              if (elements && elements.length > 0) {
                const index = elements[0].index;
                if (index !== undefined && currentData.labels && index < currentData.labels.length) {
                  handleDataPointClick(currentData.labels[index]);
                }
              }
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context: {dataset: {label: string}, parsed: {y?: number} & {[key: string]: number}}) => {
                    const label = context.dataset.label || '';
                    const value = context.parsed.y || context.parsed;
                    return `${label}: ${value}${currentLevel < maxLevel ? ' (Click to drill down)' : ''}`;
                  }
                }
              }
            }
          }}
        />
      </ChartContainer>
    </Container>
  );
};

export default DrillDownVisualization;